import React from 'react';
import axios from 'helpers/axios';
import { format } from 'date-fns';

import { UserLayout } from 'ui/components/layouts';
import {
  Title,
  Container,
  Label,
  Paragraph,
  ResizableModal,
  ModalPosition,
  Checkbox,
  Button,
} from 'ui/components/styled';
import { ConfirmationModal } from 'ui/components/styled/NewModal/ConfirmationModal';
import {
  CardGrid,
  Card,
  Icon,
  EmailModal,
  DropDownMenu,
  DropDownMenuItem,
  IconButton,
  BackToTop,
} from 'ui/components/shared';
import { getNumberOfPages, recalculatePage } from 'ui/components/shared/DataTable/helpers';
import {
  useToasts,
  useOpenState,
  useCompany,
  useMutableRef,
  useLayout,
  useCancelToken,
} from 'hooks';
import { AddJobToMatchOrphan } from './forms';
import { truncate } from 'helpers/parser';

export const Orphans = () => {
  const containerRef = React.useRef();
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [total, setTotal] = React.useState(0);
  const [perPage] = React.useState(10);
  const [search, setSearch] = React.useState('');
  const [modal, openModal, closeModal] = useOpenState();
  const { errorToast, removeAllToasts } = useToasts();
  const { setLayoutLoading } = useLayout();
  const { activeCompany, isCompanyAdmin, companiesLoaded } = useCompany();
  const [selected, setSelected] = React.useState([]);
  const [selectAll, setSelectAll] = React.useState(false);
  const [currentPage, setCurrentPage] = useMutableRef(1);
  const { newCancelToken, cancelPreviousRequest, isCancel } = useCancelToken();
  const readonly = !isCompanyAdmin();

  const fetchEmails = async (page, searchValue = search, silent = false) => {
    if (!silent) setLoading(true);

    try {
      const top = page * perPage;
      const skip = 0;
      const response = await axios.get(
        `/emails/orphans/?$top=${top}&$skip=${skip}&$search=${searchValue}&$cid=${activeCompany}&$orderby=date&$order=desc`
      );

      if (response.status === 200) {
        setData(response.data.value);
        setTotal(response.data.count);
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Fetch Emails.' });
    }

    setLoading(false);
  };

  const handleFilter = async (value) => {
    setLoading(true);
    setData([]);
    setCurrentPage(1);
    setSearch(value);

    cancelPreviousRequest();

    try {
      const skip = 0;
      const response = await axios.get(
        `/emails/orphans/?$top=${perPage}&$skip=${skip}&$search=${value}&$cid=${activeCompany}&$orderby=date&$order=desc`,
        { cancelToken: newCancelToken() }
      );

      if (response.status === 200) {
        setData(response.data.value);
        setTotal(response.data.count);
        resetSelection();
      }
    } catch (error) {
      if (isCancel(error)) return;
      errorToast({ error, default: 'Error: Filter Emails.' });
    }

    setLoading(false);
  };

  const removeOrphanEmail = async (email) => {
    try {
      const response = await axios.delete(`/emails/${email.id}`);

      if (response.status === 204) {
        removeAllToasts();
        refresh({ newCount: total - 1 });
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Remove Orphan.' });
    }

    closeModal();
  };

  const removeSelectedOrphans = async () => {
    try {
      const response = await axios.post('/emails/orphans/batch-delete', { ids: selected });

      if (response.status === 200) {
        removeAllToasts();
        refresh({ clearSearch: true });
        resetSelection();
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Remove Selected Orphans.' });
    }

    closeModal();
  };

  const removeAllOrphans = async () => {
    try {
      const response = await axios.post(
        `/emails/orphans/delete-all?$search=${search}&$cid=${activeCompany}`
      );

      if (response.status === 200) {
        removeAllToasts();
        refresh({ clearSearch: true });
        resetSelection();
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Remove All Orphans.' });
    }

    closeModal();
  };

  const refresh = ({ newCount, clearSearch = false } = {}) => {
    if (newCount > 0) {
      const updatedPage = getNumberOfPages(newCount, perPage);
      const recalculatedPage = recalculatePage(currentPage.current, updatedPage);
      if (recalculatedPage !== currentPage.current) {
        setCurrentPage(recalculatedPage);
      }
    }

    if (clearSearch) {
      setSearch('');
    }

    fetchData(currentPage.current, clearSearch ? '' : search);
  };

  const handleRefresh = async (backToTop = true) => {
    setLayoutLoading(true);
    await fetchEmails(currentPage.current, search, true);
    setLayoutLoading(false);
    if (backToTop) {
      containerRef.current.scrollTo(0, 0);
    }
  };

  const handleLoadMore = () => {
    fetchData(currentPage.current + 1);
  };

  const fetchData = (page = 1, searchValue = search) => {
    setCurrentPage(page);
    if (page === 1) {
      setData([]);
    }
    fetchEmails(page, searchValue);
  };

  const resetSelection = () => {
    setSelected([]);
    setSelectAll(false);
  };

  React.useEffect(() => {
    if (companiesLoaded) {
      fetchData();
    }
  }, [companiesLoaded, activeCompany]);

  const handleSelectChange = (event) => {
    const { value, checked } = event.target;
    if (checked) {
      setSelected([...selected, value]);
    } else {
      setSelected(selected.filter((id) => id !== value));
    }
  };

  const renderCard = React.useCallback(
    (email) => {
      const isProcessing = email.lockedAt && email.lockReason === 'orphan-processing';
      const processingTooltip =
        'This orphan is being processed. This action have probably been triggered by another user, please retry later.';

      return (
        <Card height="200px" cursor="pointer" onClick={() => openModal('view-email', { email })}>
          <Container flexDirection="row" gap="15px" px="m" pl={readonly ? 'm' : 's'} py="m">
            {!readonly && (
              <Container width="auto" pt="2px">
                <Checkbox
                  value={email.id}
                  checked={selected.includes(email.id)}
                  onChange={handleSelectChange}
                />
              </Container>
            )}
            <Container width="calc(100% - 30px)">
              <Container flexDirection="row" justifyContent="space-between" height="auto">
                <Container>
                  <Container flexDirection="row" alignItems="center" height="auto" mb="xs">
                    <Label mr="xxs">From:</Label>
                    <Paragraph>{email.from}</Paragraph>
                  </Container>
                  <Container flexDirection="row" alignItems="center" height="auto" mb="xs">
                    <Label mr="xxs">Subject:</Label>
                    <Paragraph>{email.subject}</Paragraph>
                  </Container>
                  <Container flexDirection="row" alignItems="center" height="auto" mb="s">
                    <Label mr="xxs">Received:</Label>
                    <Paragraph>
                      {format(new Date(email.date), 'MMMM do yyyy, h:mm:ss aaa')}
                    </Paragraph>
                  </Container>
                </Container>
                <Container flexDirection="row" width="auto" gap="10px">
                  {!readonly && (
                    <CustomDropdownMenu>
                      <DropDownMenuItem
                        onClick={() => openModal('add-job', { email })}
                        disabled={isProcessing}
                        title={isProcessing ? processingTooltip : ''}
                      >
                        <Icon icon="plus" size="12px" mr="9px" /> Add monitor to match orphan
                      </DropDownMenuItem>
                      <DropDownMenuItem
                        onClick={() => openModal('confirm-remove-orphan', { email })}
                        disabled={isProcessing}
                        title={isProcessing ? processingTooltip : ''}
                      >
                        <Icon icon="delete" size="16px" mr="7px" /> Delete
                      </DropDownMenuItem>
                    </CustomDropdownMenu>
                  )}
                </Container>
              </Container>
              <Paragraph color="#333" lineHeight="24px" lineClamp={3}>
                {truncate(email.bodyAsText || email.body)}
              </Paragraph>
            </Container>
          </Container>
        </Card>
      );
    },
    [readonly, handleSelectChange]
  );

  const handleSelectAllChange = () => {
    const value = !selectAll;
    setSelectAll(value);
    if (value) {
      setSelected(data.map(({ id }) => id));
    } else {
      setSelected([]);
    }
  };

  const renderSubHeader = () =>
    data?.length > 0 &&
    !readonly && (
      <Container
        flexDirection="row"
        alignItems="center"
        minHeight="36px"
        height="auto"
        px="m"
        pr="l"
        pb="s"
      >
        <Checkbox
          label={selectAll ? 'Deselect all' : 'Select all'}
          labelColor={selectAll ? 'primary' : 'grey'}
          checked={selectAll}
          onChange={handleSelectAllChange}
        />
        {selected.length > 0 && (
          <>
            <Button
              variant="warning"
              borderRadius="3px"
              width="auto"
              boxShadow="none"
              fontSize="13px"
              padding="3px 9px"
              onClick={() => openModal('confirm-remove-selected-orphans')}
              disabled={loading}
              ml="m"
            >
              Delete selected ({selected.length})
            </Button>
            <Button
              variant="danger"
              borderRadius="3px"
              width="auto"
              boxShadow="none"
              fontSize="13px"
              padding="3px 9px"
              onClick={() => openModal('confirm-remove-all-orphans')}
              disabled={loading}
              ml="m"
            >
              Delete all ({total})
            </Button>
          </>
        )}
      </Container>
    );

  return (
    <UserLayout ref={containerRef}>
      <Title variant="dark" mx="m" mb="l">
        Orphans {loading ? '' : `(${total})`}
      </Title>
      <CardGrid
        data={data}
        total={total}
        loading={loading}
        cardRenderer={renderCard}
        filterPlaceholder="Emails Search"
        filterValue={search}
        onFilter={handleFilter}
        onLoadMore={handleLoadMore}
        currentPage={currentPage.current}
        containerRef={containerRef}
        overflow="initial"
        headerProps={{ pb: readonly ? 'm' : 's' }}
        headerActions={
          <IconButton
            icon="refresh"
            title="Refresh"
            iconProps={{ size: '16px' }}
            onClick={handleRefresh}
          />
        }
        headerDisposition="actions-after-filter"
        subHeader={renderSubHeader}
        showCount
      />
      <BackToTop scrollContainerRef={containerRef} />
      <EmailModal
        show={modal.isOpen('view-email')}
        onClose={closeModal}
        email={modal.params?.email}
      />
      <ResizableModal
        show={modal.isOpen('add-job')}
        onClose={closeModal}
        position={ModalPosition.TOP_CENTER}
        top="7%"
        width="auto"
        minWidth="700px"
        resize="horizontal"
      >
        {modal.isOpen('add-job') && (
          <AddJobToMatchOrphan
            orphan={modal.params?.email}
            onClose={closeModal}
            onSuccess={refresh}
          />
        )}
      </ResizableModal>
      <ConfirmationModal
        show={modal.isOpen('confirm-remove-orphan')}
        onClose={closeModal}
        onConfirm={() => removeOrphanEmail(modal.params?.email)}
      >
        Are you sure you want to remove this orphan ?
      </ConfirmationModal>
      <ConfirmationModal
        show={modal.isOpen('confirm-remove-selected-orphans')}
        onClose={closeModal}
        onConfirm={removeSelectedOrphans}
      >
        Are you sure you want to delete the selected orphans ({selected.length}) ?
      </ConfirmationModal>
      <ConfirmationModal
        show={modal.isOpen('confirm-remove-all-orphans')}
        onClose={closeModal}
        onConfirm={removeAllOrphans}
      >
        Are you sure you want to delete all the orphans ({total}) ?
      </ConfirmationModal>
    </UserLayout>
  );
};

const CustomDropdownMenu = (props) => (
  <DropDownMenu
    width={250}
    gap="10px"
    fixed={false}
    iconButtonProps={{
      variant: 'rounded',
      bg: '#f5f9ff',
      size: '28px',
      top: '-2px',
      mt: '-2px',
      filter: 'drop-shadow(0px 3px 6px rgba(0, 0, 0, 0.16))',
      iconProps: { color: '#3591fc', width: 16, height: 4 },
    }}
    {...props}
  />
);
